import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, TrendingUp, TrendingDown, Plus, ExternalLink, Building2, ArrowUpDown, Package } from 'lucide-react';
import { Button, StatusBadge, CardStats } from '@components/ui';
import { getMovementsByAccountId, MOVEMENT_TYPE_LABELS, MOVEMENT_SUBTYPE_LABELS } from '@mocks/cariHesaplar';
import HareketModal from './HareketModal';
import clsx from 'clsx';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });

export default function CariHesapDetay({ account, constructionId, onClose, onMovementAdded }) {
    const [hareketModalOpen, setHareketModalOpen] = useState(false);
    const [defaultHareketType, setDefaultHareketType] = useState('purchase');

    if (!account) return null;

    const movements = getMovementsByAccountId(account.id, constructionId || null);
    // Sort newest first
    const sortedMovements = [...movements].sort((a, b) => new Date(b.date) - new Date(a.date));

    const openHareket = (type) => {
        setDefaultHareketType(type);
        setHareketModalOpen(true);
    };

    const handleMovementAdded = (data) => {
        onMovementAdded?.(data);
        setHareketModalOpen(false);
    };

    // Compute stats from movements (respect constructionId filter)
    const filteredPurchases = movements.filter(m => m.type === 'purchase').reduce((s, m) => s + m.amount, 0);
    const filteredPayments = movements.filter(m => m.type === 'payment').reduce((s, m) => s + m.amount, 0);
    const filteredBalance = constructionId ? filteredPurchases - filteredPayments : account.currentBalance;

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />

            {/* Slide-over Panel */}
            <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-bg-primary z-50 shadow-2xl flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between px-6 py-5 border-b border-border-light shrink-0">
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-text-primary">{account.companyName}</h2>
                            <StatusBadge
                                status={account.companyTypeLabel}
                                variant={account.companyType === 'supplier' ? 'info' : account.companyType === 'subcontractor' ? 'warning' : 'secondary'}
                                size="xs"
                            />
                        </div>
                        <p className="text-sm text-text-tertiary mt-0.5">{account.companyFullName}</p>
                        {constructionId && (
                            <p className="text-xs text-primary mt-1 flex items-center gap-1">
                                <Building2 size={12} />
                                Proje bazlı görünüm
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to={`/finans/firmalar/${account.companyId}`}
                            className="p-2 rounded-lg hover:bg-bg-tertiary transition-colors text-text-muted hover:text-primary"
                            title="Firma Kartına Git"
                        >
                            <ExternalLink size={18} />
                        </Link>
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-bg-tertiary transition-colors text-text-muted">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 px-6 py-4 bg-bg-secondary shrink-0">
                    <div className="bg-bg-primary rounded-xl p-3 text-center">
                        <p className="text-xs text-text-tertiary mb-1">Toplam Alış</p>
                        <p className="text-base font-bold text-error">{formatCurrency(filteredPurchases)}</p>
                    </div>
                    <div className="bg-bg-primary rounded-xl p-3 text-center">
                        <p className="text-xs text-text-tertiary mb-1">Toplam Ödeme</p>
                        <p className="text-base font-bold text-success">{formatCurrency(filteredPayments)}</p>
                    </div>
                    <div className={clsx(
                        'rounded-xl p-3 text-center',
                        filteredBalance > 0 ? 'bg-error/10' : 'bg-success/10'
                    )}>
                        <p className="text-xs text-text-tertiary mb-1">Güncel Bakiye</p>
                        <p className={clsx(
                            'text-base font-bold',
                            filteredBalance > 0 ? 'text-error' : 'text-success'
                        )}>
                            {formatCurrency(filteredBalance)}
                        </p>
                    </div>
                </div>

                {/* Movement List */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {sortedMovements.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <ArrowUpDown size={40} className="text-text-muted mb-3" />
                            <p className="text-text-tertiary">Henüz hareket kaydı bulunmuyor.</p>
                            <Button
                                variant="primary"
                                icon={<Plus size={16} />}
                                className="mt-4"
                                onClick={() => openHareket('purchase')}
                            >
                                İlk Hareketi Ekle
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {/* Table Header — Desktop */}
                            <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                                <div className="col-span-2">Tarih</div>
                                <div className="col-span-4">Açıklama</div>
                                <div className="col-span-2 text-right">Borç</div>
                                <div className="col-span-2 text-right">Ödeme</div>
                                <div className="col-span-2 text-right">Bakiye</div>
                            </div>

                            {sortedMovements.map((movement, idx) => (
                                <div
                                    key={movement.id}
                                    className={clsx(
                                        'rounded-xl p-3 border transition-colors',
                                        movement.subtype === 'hakedis'
                                            ? 'bg-purple-500/5 border-purple-500/20'
                                            : movement.type === 'purchase'
                                                ? 'bg-error/5 border-error/20'
                                                : 'bg-success/5 border-success/20'
                                    )}
                                >
                                    {/* Mobile Layout */}
                                    <div className="md:hidden">
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {movement.type === 'purchase'
                                                    ? <TrendingUp size={16} className="text-error shrink-0" />
                                                    : <TrendingDown size={16} className="text-success shrink-0" />
                                                }
                                                <p className="text-sm font-medium text-text-primary truncate">{movement.description}</p>
                                            </div>
                                            <p className={clsx(
                                                'text-sm font-bold shrink-0',
                                                movement.subtype === 'hakedis' ? 'text-purple-500'
                                                    : movement.type === 'purchase' ? 'text-error' : 'text-success'
                                            )}>
                                                {movement.type === 'purchase' ? '+' : '-'}{formatCurrency(movement.amount)}
                                            </p>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-text-tertiary">
                                            <div className="flex items-center gap-2">
                                                <span>{formatDate(movement.date)}</span>
                                                <span className={clsx(
                                                    'px-1.5 py-0.5 rounded text-text-muted',
                                                    movement.subtype === 'hakedis' ? 'bg-purple-500/10 text-purple-500 font-semibold' : 'bg-bg-primary'
                                                )}>
                                                    {MOVEMENT_SUBTYPE_LABELS[movement.subtype] || movement.subtype}
                                                </span>
                                                {movement.constructionName && (
                                                    <span className="text-primary/70">{movement.constructionName}</span>
                                                )}
                                                {movement.material_order_id && (
                                                    <Link to="/insaat" onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 text-primary font-semibold hover:underline">
                                                        <Package size={9} /> Sipariş
                                                    </Link>
                                                )}
                                            </div>
                                            <span className="font-medium text-text-secondary">
                                                Bakiye: {formatCurrency(movement.balanceAfter)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Desktop Grid Layout */}
                                    <div className="hidden md:grid grid-cols-12 gap-2 items-center text-sm">
                                        <div className="col-span-2 text-text-tertiary text-xs">{formatDate(movement.date)}</div>
                                        <div className="col-span-4 min-w-0">
                                            <div className="flex items-center gap-2">
                                                {movement.type === 'purchase'
                                                    ? <TrendingUp size={14} className={movement.subtype === 'hakedis' ? 'text-purple-500 shrink-0' : 'text-error shrink-0'} />
                                                    : <TrendingDown size={14} className="text-success shrink-0" />
                                                }
                                                <div className="min-w-0">
                                                    <p className="font-medium text-text-primary truncate">{movement.description}</p>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        <span className={clsx(
                                                            'text-xs',
                                                            movement.subtype === 'hakedis' ? 'text-purple-500 font-semibold' : 'text-text-tertiary'
                                                        )}>
                                                            {MOVEMENT_SUBTYPE_LABELS[movement.subtype]}
                                                        </span>
                                                        {movement.constructionName && !constructionId && (
                                                            <span className="text-xs text-primary/70 truncate">
                                                                • {movement.constructionName}
                                                            </span>
                                                        )}
                                                        {movement.material_order_id && (
                                                            <Link to="/insaat" onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 text-xs text-primary font-semibold hover:underline">
                                                                <Package size={9} /> Sipariş
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-span-2 text-right font-medium text-error">
                                            {movement.type === 'purchase' ? formatCurrency(movement.amount) : '—'}
                                        </div>
                                        <div className="col-span-2 text-right font-medium text-success">
                                            {movement.type === 'payment' ? formatCurrency(movement.amount) : '—'}
                                        </div>
                                        <div className={clsx(
                                            'col-span-2 text-right font-bold text-sm',
                                            movement.balanceAfter > 0 ? 'text-error' : 'text-success'
                                        )}>
                                            {formatCurrency(movement.balanceAfter)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-border-light flex gap-3 shrink-0">
                    <Button
                        variant="error"
                        icon={<TrendingUp size={16} />}
                        onClick={() => openHareket('purchase')}
                        className="flex-1"
                    >
                        Alış / Fatura Ekle
                    </Button>
                    <Button
                        variant="success"
                        icon={<TrendingDown size={16} />}
                        onClick={() => openHareket('payment')}
                        className="flex-1"
                    >
                        Ödeme Ekle
                    </Button>
                </div>
            </div>

            <HareketModal
                isOpen={hareketModalOpen}
                onClose={() => setHareketModalOpen(false)}
                onSubmit={handleMovementAdded}
                defaultAccountId={account.id}
                defaultType={defaultHareketType}
            />
        </>
    );
}
